import { useState, useEffect, useRef } from 'react';
import styles from './Oracle.module.css';

// 自动检测 API 地址
const getApiUrl = () => {
  if (process.env.REACT_APP_ORACLE_API) {
    return process.env.REACT_APP_ORACLE_API;
  }
  // 生产环境：同域名，用相对路径
  if (process.env.NODE_ENV === 'production') {
    return '';
  }
  // 开发环境：如果是手机访问，使用当前主机地址
  const host = window.location.hostname;
  if (host !== 'localhost' && host !== '127.0.0.1') {
    return `http://${host}:3001`;
  }
  return 'http://localhost:3001';
};

const API_URL = getApiUrl();

// 快捷问题
const quickQuestions = [
  '最近有点迷茫',
  '怎么发挥我的优势？',
  '压力有点大',
];

export default function Oracle({ archetype, archetypeInfo, onClose, isFullPage = false }) {
  const [messages, setMessages] = useState([
    { role: 'oracle', content: `星辰指引你来到此处，${archetypeInfo?.planet || '宇宙'}的旅者。我已在你的星轨中感知到一些涟漪...有什么想问的？` }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [stars, setStars] = useState([]);
  const messagesEndRef = useRef(null);

  // 生成背景星星
  useEffect(() => {
    const generated = Array.from({ length: 30 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 2 + 1,
      delay: Math.random() * 3,
    }));
    setStars(generated);
  }, []);

  // 自动滚动到底部
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const handleSend = async (customMessage) => {
    const messageToSend = customMessage || input.trim();
    if (!messageToSend || loading) return;
    
    if (!customMessage) setInput('');
    setMessages(prev => [...prev, { role: 'user', content: messageToSend }]);
    setLoading(true);

    try {
      const historyToSend = messages.slice(1);
      
      const res = await fetch(`${API_URL}/api/oracle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: messageToSend,
          archetype,
          archetypeInfo: archetypeInfo ? {
            name: archetypeInfo.name,
            planet: archetypeInfo.planet,
            feature: archetypeInfo.feature,
          } : null,
          history: historyToSend,
        }),
      });

      const data = await res.json();
      
      if (data.error) {
        setMessages(prev => [...prev, { role: 'oracle', content: data.error }]);
      } else {
        setMessages(prev => [...prev, { role: 'oracle', content: data.reply }]);
      }
    } catch (err) {
      setMessages(prev => [...prev, { 
        role: 'oracle', 
        content: '星际通讯暂时中断...请确保后端服务已启动' 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className={`${styles.overlay} ${isFullPage ? styles.fullPage : ''}`} onClick={isFullPage ? undefined : onClose}>
      {/* 背景星星 */}
      <div className={styles.starsContainer}>
        {stars.map(star => (
          <div
            key={star.id}
            className={styles.star}
            style={{
              left: `${star.x}%`,
              top: `${star.y}%`,
              width: `${star.size}px`,
              height: `${star.size}px`,
              animationDelay: `${star.delay}s`,
            }}
          />
        ))}
      </div>

      <div className={styles.container} onClick={e => e.stopPropagation()}>
        {/* 装饰角落 */}
        <div className={styles.corner} data-pos="tl" />
        <div className={styles.corner} data-pos="tr" />
        <div className={styles.corner} data-pos="bl" />
        <div className={styles.corner} data-pos="br" />

        {/* 头部 */}
        <div className={styles.header}>
          <div className={styles.titleArea}>
            <div className={styles.iconWrapper}>
              <img src="/images/jisi.png" alt="星际祭司" className={styles.icon} />
              <div className={styles.iconGlow} />
            </div>
            <div className={styles.titleText}>
              <h2>星际祭司</h2>
              <span className={styles.subtitle}>✦ 宇宙智慧引路人 ✦</span>
            </div>
          </div>
          <button className={styles.closeBtn} onClick={onClose}>×</button>
        </div>

        {/* 用户原型信息 */}
        {archetypeInfo && (
          <div className={styles.userInfo}>
            <img src={archetypeInfo.planetImg} alt="" className={styles.userPlanet} />
            <div className={styles.userDetails}>
              <span className={styles.userLabel}>你的原型</span>
              <span className={styles.userName}>{archetypeInfo.name} · {archetypeInfo.planet}</span>
            </div>
          </div>
        )}

        {/* 消息区域 */}
        <div className={styles.messages}>
          {messages.map((msg, i) => (
            <div key={i} className={`${styles.message} ${styles[msg.role]}`}>
              {msg.role === 'oracle' && (
                <div className={styles.avatarWrapper}>
                  <img src="/images/jisi.png" alt="" className={styles.avatar} />
                </div>
              )}
              <div className={styles.messageContent}>
                <p>{msg.content}</p>
                <span className={styles.messageTime}>
                  {msg.role === 'oracle' ? '✦' : '◇'}
                </span>
              </div>
            </div>
          ))}
          {loading && (
            <div className={`${styles.message} ${styles.oracle}`}>
              <div className={styles.avatarWrapper}>
                <img src="/images/jisi.png" alt="" className={styles.avatar} />
              </div>
              <div className={styles.messageContent}>
                <div className={styles.loadingContainer}>
                  {/* 星座旋转动画 */}
                  <div className={styles.constellationLoader}>
                    <div className={styles.orbitRing}>
                      <div className={styles.orbitStar} style={{ '--delay': '0s', '--angle': '0deg' }} />
                      <div className={styles.orbitStar} style={{ '--delay': '0.2s', '--angle': '60deg' }} />
                      <div className={styles.orbitStar} style={{ '--delay': '0.4s', '--angle': '120deg' }} />
                      <div className={styles.orbitStar} style={{ '--delay': '0.6s', '--angle': '180deg' }} />
                      <div className={styles.orbitStar} style={{ '--delay': '0.8s', '--angle': '240deg' }} />
                      <div className={styles.orbitStar} style={{ '--delay': '1s', '--angle': '300deg' }} />
                    </div>
                    <div className={styles.centerGlow} />
                  </div>
                  <div className={styles.loadingText}>
                    <span className={styles.loadingDot}>✦</span>
                    <span>星象解读中</span>
                    <span className={styles.loadingDot}>✦</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* 快捷问题 */}
        {messages.length <= 2 && (
          <div className={styles.quickQuestions}>
            <span className={styles.quickLabel}>快捷提问</span>
            <div className={styles.quickList}>
              {quickQuestions.map((q, i) => (
                <button
                  key={i}
                  className={styles.quickBtn}
                  onClick={() => handleSend(q)}
                  disabled={loading}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* 输入区域 */}
        <div className={styles.inputArea}>
          <div className={styles.inputWrapper}>
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="向星际祭司倾诉你的困惑..."
              rows={2}
              disabled={loading}
            />
          </div>
          <button 
            className={styles.sendBtn}
            onClick={() => handleSend()} 
            disabled={loading || !input.trim()}
          >
            <span className={styles.sendIcon}>✦</span>
            <span>发送</span>
          </button>
        </div>

        {/* 底部装饰 */}
        <div className={styles.footer}>
          <div className={styles.footerLine} />
          <span>COSMIC ORACLE</span>
          <div className={styles.footerLine} />
        </div>
      </div>
    </div>
  );
}
